import React from "react";
import { Building2, Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function BranchCounterSelectors({ className, showCounter = true }) {
  const { t } = useTranslation();
  const { user, updateContext, contextLoading } = useAuth();

  if (!user) return null;

  const multiBranch = isMultiBranchFeatureEnabled(user);
  const branchId = user.default_branch?.id ? String(user.default_branch.id) : "";
  const counterId = user.default_counter?.id ? String(user.default_counter.id) : "";
  const selectedCounter = user.counters?.find((c) => String(c.id) === counterId);
  const shiftOpen = selectedCounter?.shift_status === "open" || user.default_counter?.shift_status === "open";
  const branches = user.branches || [];

  async function onBranchChange(value) {
    const branch = branches.find((b) => String(b.id) === value);
    try {
      await updateContext(Number(value), null);
      toast.success(`Branch: ${branch?.name || "updated"}`);
    } catch (err) {
      toast.error(err.response?.data?.errors?.branch_id?.[0] || "Could not switch branch");
    }
  }

  async function onCounterChange(value) {
    if (!branchId) return;
    try {
      await updateContext(Number(branchId), Number(value));
      const counter = user.counters?.find((c) => String(c.id) === value);
      toast.success(`Counter: ${counter?.name || "updated"}`);
    } catch {
      toast.error("Could not switch counter");
    }
  }

  const chipTrigger =
    "h-8 rounded-lg text-[12px] bg-white border-[#E8D5C8] shadow-sm focus:ring-2 focus:ring-brand-orange/25 focus:border-brand-orange/40";

  const showBranch = multiBranch && branches.length > 0;
  if (!showBranch && !showCounter) return null;

  return (
    <div className={cn("hidden md:flex items-center gap-2", className)}>
      {showBranch ? (
        <Select
          value={branchId}
          onValueChange={onBranchChange}
          disabled={contextLoading || branches.length < 2}
        >
          <SelectTrigger
            className={cn(chipTrigger, "w-[150px]")}
            data-testid="branch-selector"
          >
            <Building2 size={13} className="mr-1.5 text-brand-orange shrink-0" />
            <SelectValue placeholder="Branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.map((b) => (
              <SelectItem key={b.id} value={String(b.id)} className="text-[12px]">
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {showCounter ? (
        <>
          <Select value={counterId} onValueChange={onCounterChange} disabled={contextLoading || !branchId}>
            <SelectTrigger
              className={cn(chipTrigger, "w-[140px]")}
              data-testid="counter-selector"
            >
              <Monitor size={13} className="mr-1.5 text-brand-orange shrink-0" />
              <SelectValue placeholder="Counter" />
            </SelectTrigger>
            <SelectContent>
              {(user.counters || []).map((c) => (
                <SelectItem key={c.id} value={String(c.id)} className="text-[12px]">
                  <span className="flex items-center justify-between gap-2 w-full">
                    <span>{c.name}</span>
                    <span className={c.shift_status === "open" ? "text-emerald-600 text-[10px]" : "text-brand-brown/40 text-[10px]"}>
                      {c.shift_status === "open" ? t("counter.shiftOpen") : t("counter.shiftClosed")}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {counterId ? (
            <span
              className={cn(
                "hidden lg:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border",
                shiftOpen
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200",
              )}
              title={t("counter.terminalHint")}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  shiftOpen ? "bg-emerald-500 animate-pulse" : "bg-amber-500",
                )}
              />
              {shiftOpen ? t("counter.shiftOpen") : t("counter.shiftClosed")}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
